<template>
  <!-- 问卷页面 - 参照注册页面的滑动实现 -->
  <div
    style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; overflow-y: scroll; overflow-x: hidden; -webkit-overflow-scrolling: touch; touch-action: pan-y; background-color: #f9fafb;">
    <div style="padding: 1rem; min-height: 100vh; padding-bottom: 4rem;">
      <!-- 头部导航 -->
      <div class="bg-white shadow-sm border-b border-gray-200 rounded-lg mb-4">
        <div class="max-w-4xl mx-auto px-4 py-4">
          <div class="flex items-center justify-between">
            <router-link to="/"
              class="inline-flex items-center gap-2 text-gray-600 hover:text-primary transition-colors">
               <img style="width: 60px;" src="../assets/images/back_icon.png">
            </router-link>

            <h1 class="text-lg font-semibold text-gray-900">
              Carbon Footprint Calculator
            </h1>

            <div class="w-16"></div> <!-- Spacer for centering -->
          </div>

          <!-- 进度条 -->
          <div class="mt-4">
            <div class="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Progress</span>
              <span>{{ answeredQuestions }}/{{ totalQuestions }}</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div class="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
                :style="{ width: `${progress}%` }"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- 主要内容 -->
      <div class="max-w-4xl mx-auto px-4">
        <!-- 加载状态 -->
        <div v-if="loading && categories.length === 0" class="flex items-center justify-center py-12">
          <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p class="text-gray-600">Loading questionnaire...</p>
          </div>
        </div>

        <!-- 问卷内容 -->
        <div v-else-if="currentQuestion && !questionnaireResult" class="space-y-6">

          <!-- 问题卡片 -->
          <QuestionCard :question="currentQuestion" :answer="getAnswer(currentQuestion.id)" @answer="handleAnswer" />

          <!-- 导航按钮 -->
          <div class="flex items-center justify-between pt-6">
            <button @click="previousQuestion" :disabled="currentCategoryIndex === 0 && currentQuestionIndex === 0"
              class="btn-white">
              Previous
            </button>

            <div class="flex gap-3">
              <button v-if="!isLastQuestion" @click="nextQuestion" :disabled="!canGoNext"
                class="btn-full-gree">
                Next
              </button>

              <button v-else @click="handleSubmit" :disabled="!canGoNext || loading"
                class="btn-full-gree">
                <div v-if="loading" class="flex items-center gap-2">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Calculating...</span>
                </div>
                <span v-else>Calculate Results</span>
              </button>
            </div>
          </div>
        </div>

        <!-- 问卷提交完成，等待弹窗 -->
        <div v-else-if="questionnaireResult" class="text-center py-12">
          <div class="bg-white rounded-lg p-8 shadow-sm border border-gray-200 max-w-md mx-auto">
            <div class="text-6xl mb-4">🌱</div>
            <h2 class="text-2xl font-bold text-gray-900 mb-2">Calculation Complete!</h2>
            <p class="text-gray-600 mb-6">
              Your carbon footprint has been calculated successfully.
            </p>
            <button @click="showResultModal = true"
              class="btn-full-gree">
              View Results
            </button>
          </div>
        </div>

        <!-- 完成状态 -->
        <div v-else-if="results" class="text-center py-12">
          <div class="bg-white rounded-lg p-8 shadow-sm border border-gray-200 max-w-md mx-auto">
            <div class="text-6xl mb-4">🌱</div>
            <h2 class="text-2xl font-bold text-gray-900 mb-2">Calculation Complete!</h2>
            <p class="text-gray-600 mb-6">
              Your carbon footprint has been calculated successfully.
            </p>
            <router-link :to="`/questionnaire/results/${results.response_id}`"
              class="btn btn-primary w-full transform transition-all duration-200 hover:scale-105 active:scale-95">
              View Results
            </router-link>
          </div>
        </div>

        <!-- 错误状态 -->
        <div v-else class="text-center py-12">
          <div class="text-6xl mb-4">😕</div>
          <h2 class="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p class="text-gray-600 mb-6">
            We couldn't load the questionnaire. Please try again.
          </p>
          <button @click="() => fetchQuestionnaire()"
            class="btn-full-gree">
            Retry
          </button>
        </div>
      </div>
    </div>



    <!-- 结果弹窗 -->
    <QuestionnaireResultModal v-if="questionnaireResult" :show="showResultModal" :result="questionnaireResult"
      :allow-close="true" @close="handleResultModalClose" @update:show="showResultModal = $event" />
  </div>
</template>

<script setup lang="ts">
  import { onMounted, ref, nextTick, onBeforeUnmount } from 'vue'
  import { useRouter } from 'vue-router'
  import { useMessage } from 'naive-ui'
  import { storeToRefs } from 'pinia'
  import { useQuestionnaireStore, type QuestionAnswer } from '@/stores/questionnaire'
  import { useAuthStore } from '@/stores/auth'
  import QuestionCard from '@/components/questionnaire/QuestionCard.vue'
  import QuestionnaireResultModal from '@/components/questionnaire/QuestionnaireResultModal.vue'

  const router = useRouter()
  const message = useMessage()
  const questionnaireStore = useQuestionnaireStore()
  const authStore = useAuthStore()

  // 结果弹窗状态
  const showResultModal = ref(false)
  const questionnaireResult = ref<any>(null)

  const {
    categories,
    // currentCategory,
    currentQuestion,
    currentCategoryIndex,
    currentQuestionIndex,
    totalQuestions,
    answeredQuestions,
    progress,
    canGoNext,
    isLastQuestion,
    loading,
    results
  } = storeToRefs(questionnaireStore)

  const {
    fetchQuestionnaire,
    getAnswer,
    setAnswer,
    nextQuestion,
    previousQuestion,
    submitQuestionnaire
  } = questionnaireStore

  // 处理答案变化
  const handleAnswer = (questionId: number, answer: QuestionAnswer) => {
    setAnswer(questionId, answer)
  }

  // 处理提交
  const handleSubmit = async () => {
    try {
      console.log('开始提交问卷...')
      const result = await submitQuestionnaire()
      console.log('问卷提交成功，结果:', result)

      // 标记问卷已完成
      questionnaireCompleted.value = true
      if (authStore.isAuthenticated) {
        await authStore.markQuestionnaireCompleted({
          total_carbon_footprint: result.total_carbon_footprint,
          category_breakdown: result.category_breakdown || {}
        })
        console.log('已标记问卷为完成状态')
      }

      // 确保结果数据正确设置
      if (result && result.response_id) {
        console.log('设置问卷结果数据:', result)

        try {
          // 先设置结果数据
          questionnaireResult.value = {
            response_id: result.response_id,
            total_carbon_footprint: Number(result.total_carbon_footprint) || 0,
            session_id: result.session_id,
            category_breakdown: result.category_breakdown || {},
            completed_at: result.completed_at
          }

          console.log('问卷结果数据已设置:', questionnaireResult.value)

          // 显示成功消息
          message.success('Carbon footprint calculated successfully!')

          // 等待DOM更新后显示弹窗
          await nextTick()

          // 自动显示弹窗
          setTimeout(() => {
            console.log('自动显示结果弹窗')
            showResultModal.value = true
          }, 500)

        } catch (error) {
          console.error('设置结果数据时出错:', error)
          throw error
        }

      } else {
        console.error('问卷结果数据无效:', result)
        message.error('Failed to get questionnaire results')

        // 即使没有结果数据，也要跳转
        if (authStore.isAuthenticated) {
          router.push('/dashboard')
        } else {
          router.push('/login')
        }
      }

    } catch (error: unknown) {
      console.error('问卷提交失败:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to calculate carbon footprint'
      message.error(errorMessage)
    }
  }

  // 查看详细结果
  // const viewDetailedResults = () => {
  //   if (questionnaireResult.value?.response_id) {
  //     router.push(`/questionnaire/results/${questionnaireResult.value.response_id}`)
  //   } else {
  //     handleResultModalClose()
  //   }
  // }

  // 处理结果弹窗关闭
  const handleResultModalClose = () => {
    showResultModal.value = false

    console.log('弹窗关闭，检查用户认证状态:', {
      isAuthenticated: authStore.isAuthenticated,
      hasToken: !!authStore.token,
      user: authStore.user
    })

    // 根据用户登录状态决定跳转
    if (authStore.isAuthenticated) {
      // 已登录用户，跳转到仪表板
      console.log('用户已登录，跳转到仪表板')
      router.push('/dashboard')
    } else {
      // 未登录用户，跳转到登录页面
      console.log('用户未登录，跳转到登录页面')
      router.push('/login')
    }
  }

  // 标记问卷是否已完成
  const questionnaireCompleted = ref(false)

  // 处理页面离开前的清理
  const handleBeforeUnload = () => {
    // 如果问卷未完成且用户已登录，清除认证状态
    if (!questionnaireCompleted.value && authStore.isAuthenticated) {
      console.log('[QuestionnairePage] 问卷未完成，用户离开页面，清除认证状态')
      authStore.clearAuth()
    }
  }

  // 组件挂载时获取问卷数据
  onMounted(async () => {
    try {
      await fetchQuestionnaire('en')

      // 添加页面离开监听器
      window.addEventListener('beforeunload', handleBeforeUnload)
    } catch (error) {
      message.error('Failed to load questionnaire')
    }
  })

  // 组件卸载时清理
  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)

    // 如果问卷未完成，清除认证状态
    handleBeforeUnload()
  })
</script>